import { forwardRef, useState, createContext, useContext, useEffect, useRef } from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertColor, AlertProps } from '@mui/material/Alert';
import { resetErrorStore } from '../store/Error';

export interface Snack {
  message: string;
  type?: AlertColor;
  duration?: number;
  open: boolean;
}

interface SnackbarContextType {
  snack: Snack;
  setSnack: (snack: Snack) => void;
}

const SnackbarContext = createContext<SnackbarContextType>({} as SnackbarContextType);

interface SnackbarProviderProps {
  children?: React.ReactNode;
}

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return (
    <MuiAlert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});

export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const DEFAULT_SNACK: Snack = {
    message: '',
    type: 'success',
    open: false,
    duration: 5000
  };
  const [snack, setSnack] = useState<Snack>(DEFAULT_SNACK);
  const clickawayEnabledRef = useRef(false);

  const onClose = (clear = false) => {
    if (clear && snack.message) resetErrorStore();
    setSnack({ ...snack, open: false });
  };

  useEffect(() => {
    if (!snack.open) return;
    clickawayEnabledRef.current = false;

    const timer = setTimeout(() => {
      clickawayEnabledRef.current = true;
    }, 500);

    return () => clearTimeout(timer);
  }, [snack.open]);

  return (
    <SnackbarContext.Provider value={{ snack, setSnack }}>
      <Snackbar
        autoHideDuration={snack?.duration || 3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={snack?.open}
        onClose={(e, reason) => {
          if (reason === 'clickaway' && !clickawayEnabledRef.current) return;
          onClose();
        }}>
        <Alert
          onClose={() => onClose(true)}
          severity={snack?.type}
          sx={{ width: '100%' }}>
          {snack?.message}
        </Alert>
      </Snackbar>
      {children}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const { snack, setSnack } = useContext(SnackbarContext);
  return { snack, setSnack };
};
